import React from 'react';
import { T } from '../../../theme/tokens';
import type { GridConfig, KpiDef } from '../DataGrid';
import type { PermitRow, UnitRow } from '../../../data/useParcels';
import { bandColor, Pill, ProgressBar, LotCell, LinkText, DocsCell, money, moneyCompact, fmtDate, fmtWhen, isoWhen, parseDate, calendarDaysAgo, pct100, dash } from '../cells';
import { facetText, facetPerson, facetFlag, facetDate, deltaCount } from '../filterModel';
import { oxford, pctOf } from '../perspectives';

// Display names strip the sort prefix so the dock speaks the same vocabulary as the lens
// ('Critical', not '01 Critical'); ordering still follows the numeric band sequence.
// 'Immediate audit / overdue' is shortened to 'Immediate audit' — the full label ellipsized
// in the 150px Urgency pill (full text stays in the cell title).
const bandName = (b: string | null | undefined): string =>
  ((b || '').replace(/^\d+\s*/, '') || '—').replace(/^immediate audit\s*\/\s*overdue$/i, 'Immediate audit');
// Schedule pills carry county phrasing too long for a pill — shorten the display (no ellipsis),
// keep the full value in the cell title.
const shortSchedule = (s: string): string => {
  if (/not ready for scheduling/i.test(s)) return 'Not ready';
  if (/overdue\s*-\s*result not logged/i.test(s)) return 'Overdue result';
  if (/anomaly\s*-\s*expired/i.test(s)) return 'Expired anomaly';
  return s;
};
const BAND_ORDER = ['01 Critical', '02 Immediate audit / overdue', '03 In progress',
  '04 Schedule needed', '05 Monitor / not ready', '80 Closed', '90 Non-building'];
const sum = (rows: PermitRow[], f: (r: PermitRow) => number | null) => rows.reduce((s, r) => s + (f(r) || 0), 0);
// The iWorQ "Amount due" field is left null after a payment clears the balance, but the change
// feed states the fact authoritatively ("…amount due now $0"). A blank where the same row says
// "$0" reads as a bug, so surface the county's own statement as a real 0. KPI/attention logic
// reads r.amountDue directly and is unaffected; this governs only the displayed/exported cell.
const effectiveDue = (r: PermitRow): number | null =>
  r.amountDue != null ? r.amountDue : (/amount due now \$0\b/i.test(r.changeSummary || '') ? 0 : null);
const bandIs = (r: PermitRow, p: string) => (r.urgencyBand || '').slice(0, 2) === p;
// Open/Closed lifecycle pill colors (mirrors the map's Open/Closed lens palette).
const OPEN_CLOSED_COLOR: Record<string, string> = {
  Open: '#2f8f5b', 'Open / Superseded': '#C7973F', Completed: '#1f5e3a', Closed: '#b5462f',
};
const openClosedColor = (v: string): string => OPEN_CLOSED_COLOR[v] || T.inkSoft;

// A lot is "active" if a buyer is committed/in-pipeline OR it has started building — the meaningful
// scope for the no-permit gap (excludes future-phase platted lots that haven't broken ground). (P0-1)
const PIPELINE = /escrow|countersigned|waiting for request|client requested|sent to client/i;
const STARTED = new Set(['Shell', 'Rough', 'Finishes', 'Done']);
const isActiveUnit = (u: UnitRow) => PIPELINE.test(u.contractStatusLive || u.contractStatus || '') || STARTED.has(u.buildPhase || '');

// "Recently changed" from the iWorQ monitor's Last-meaningful-change timestamp (P1-1).
// parseDate (not Date.parse): the KPI must accept every format the cells render — a strict
// parse here read 0 while the grid showed rows changed '10h ago' (live repro 2026-06-12).
const recentlyChanged = (r: PermitRow) => { const t = parseDate(r.changedAt); return t > 0 && (Date.now() - t) < 48 * 3600 * 1000; };
// NEW permit = first seen by the sync inside 48h (created-time) — a different question from
// "changed": every new permit changes, but almost no changed permit is new.
const recentlyNew = (r: PermitRow) => { const t = parseDate(r.firstSeenAt); return t > 0 && (Date.now() - t) < 48 * 3600 * 1000; };
const bucketColor = (b: string) => /today/i.test(b) ? T.accent : /week/i.test(b) ? '#6fae7e' : /month/i.test(b) ? T.gold : T.inkSoft;
// The iWorQ monitor stores change summaries as semi-structured diffs. A few are clean one-liners
// ("Payment posted; amount due now $0"), but most are raw segment dumps joined by " - " — often
// truncated mid-word at the source ("- Permit Information - Issued Date: (blank) -> 2026-06-11 -
// Permit Informa"), and carrying scaffolding the operator never needs (check numbers, S3 upload
// URLs, "Make checks payable…", inspection key/value blobs). That ellipsizes acceptably in the
// 210px cell but dumps verbatim into CSV/Excel/PDF, where it is unreadable. humanizeChange parses
// the recognized segments — date-field changes, permit type, fee-due lines, notes (paid vs due vs
// neutral), inspection logs, file uploads — into a concise, ordered clause list, and sanitizes
// anything unrecognized rather than ever emitting raw scaffolding. Validated against the full live
// corpus of distinct summaries (see scripts/regression-dock-export.mjs). Exported for the harness.
const DATE_CHANGE = /(Issued|Expiration|Applied|Available) Date:\s*(.+?)\s*->\s*(\d{4}-\d{2}-\d{2})/g;
const CHANGE_RANK: Record<string, number> = { Issued: 0, Expiration: 1, Applied: 2, Available: 3 };
// Reduce a free-text note to its gist: drop the leading date stamp, the trailing "w/ Ck# N",
// and county boilerplate; keep the first sentence.
function cleanNote(t: string): string {
  return t.trim()
    .replace(/^\d{1,2}\/\d{1,2}\/\d{2,4}\s*-?\s*/, '')
    .replace(/\s*-?\s*\d{1,2}\/\d{1,2}\/\d{2,4}\s*w\/\s*Ck#?\s*\d+\.?/i, '')
    .split(/\.\s|\bMake checks payable\b/)[0]
    .replace(/^[\s\-.]+|[\s\-.]+$/g, '').replace(/\s+/g, ' ');
}
function noteGist(t: string): string {
  const c = cleanNote(t); const low = c.toLowerCase();
  if (low.includes('zoning') && low.includes('paid')) return 'zoning review fee paid';
  if (low.includes('zoning') && low.includes('fee')) {
    const m = c.match(/\$\s?([\d.]+)/); const amt = m ? ` ($${m[1]})` : '';
    return low.includes('due') ? `zoning fee due${amt}` : `zoning fee${amt}`;
  }
  return c ? c.slice(0, 48).trim() : '';
}
export function humanizeChange(raw: string | null | undefined): string {
  let s = (raw || '').trim();
  if (!s) return '';
  s = s.replace(/\\\*/g, '').replace(/\\/g, '');
  // "Electrical Permit Fee: Due; Due $255" → "Electrical permit fee due $255"
  const feeDue = s.match(/^(\w[\w ]*?) Permit Fee:\s*Due;?\s*Due\s*\$?([\d.,]+)/i);
  if (feeDue) { const k = feeDue[1].trim(); return `${k.charAt(0).toUpperCase()}${k.slice(1).toLowerCase()} permit fee due $${feeDue[2]}`; }
  // a clean one-liner with no diff scaffolding passes straight through
  if (!s.includes(' - ') && !/^(fee changed|payment posted)\s*:/i.test(s)) return s;

  const clauses: { rank: number; text: string }[] = [];
  let m: RegExpExecArray | null; DATE_CHANGE.lastIndex = 0;
  while ((m = DATE_CHANGE.exec(s))) {
    const field = m[1], oldVal = m[2].trim(), newVal = m[3];
    const isSet = oldVal === '' || /^\(blank/i.test(oldVal);
    clauses.push({ rank: CHANGE_RANK[field], text: isSet ? `${field} ${newVal}` : `${field} ${oldVal}→${newVal}` });
  }
  const type = s.match(/Permit Type:\s*\(blank\)\s*->\s*(?:\d+\s*)?([A-Za-z][^-]*?)(?=\s+-\s+|$)/);
  if (type) clauses.push({ rank: 4, text: `type set: ${type[1].trim()}` });
  if (/Inspections changed/i.test(s)) {
    const comp = s.match(/Completed Date:\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/);
    const sch = s.match(/Scheduled Date:\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/);
    clauses.push({ rank: 5, text: comp ? `inspection completed ${comp[1]}` : sch ? `inspection scheduled ${sch[1]}` : 'inspection updated' });
  }
  if (/Uploaded Files changed/i.test(s)) {
    const nm = s.match(/name:\s*([^,]+?\.pdf)/i);
    clauses.push({ rank: 7, text: nm && /survey/i.test(nm[1]) ? 'survey uploaded' : 'file uploaded' });
  }
  // notes CONFINED to the "Notes changed" block(s) so added:/removed: tokens belonging to the
  // Inspections/Uploaded-Files segments never leak in.
  const BLOCK = /Notes changed\s*-\s*(.+?)(?=\s+-\s+(?:Uploaded Files|Inspections|Permit Information) changed|$)/gi;
  const TOKEN = /(added|removed):\s*(.+?)(?=\s+-\s+(?:added|removed):|$)/gi;
  let bm: RegExpExecArray | null;
  while ((bm = BLOCK.exec(s))) {
    let tm: RegExpExecArray | null; TOKEN.lastIndex = 0;
    while ((tm = TOKEN.exec(bm[1]))) {
      const verb = tm[1].toLowerCase(), g = noteGist(tm[2]);
      if (!g) continue;
      if (verb === 'added') clauses.push({ rank: 6, text: g });
      // a removed note is usually cleanup of the old fee-due reminder already implied by an added
      // paid note — surface one only when it carries non-fee information.
      else if (!/zoning/i.test(g) && !/fee due/i.test(g)) clauses.push({ rank: 6, text: `removed: ${g}` });
    }
  }
  clauses.sort((a, b) => a.rank - b.rank);
  const body = [...new Set(clauses.map(c => c.text))].join('; ');
  let out: string;
  if (/^Payment posted/i.test(s)) out = ('Payment posted' + (body ? `; ${body}` : '')).trim();
  else if (body) out = body;
  else {  // nothing recognized → sanitized fallback, never raw scaffolding
    out = s.replace(/Fee changed:|Permit Information|Notes changed|Uploaded Files changed|Inspections changed|\(blank\)\s*->|name:[^,]+,?|url:\S+|added:|removed:/gi, ' ')
      .replace(/\s*-\s*/g, ' ').replace(/\s+/g, ' ').replace(/^[\s\-.]+|[\s\-.]+$/g, '').slice(0, 60).trim() || 'updated';
  }
  return out ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}
// A worklist KPI must be SCARCE: money actually owed, a failed inspection still open, or an
// overdue/expired schedule. (Gating alone is the NORMAL state of a live permit — 88 of 136 —
// and unpaid fee STATUS without a balance is bookkeeping, not work. Both made this 135/136.)
const needsAttention = (r: PermitRow) =>
  !r.void && ((r.amountDue || 0) > 0 || (r.failedInspections || 0) > 0 || /overdue|expired/i.test(r.schedule || ''));
// Highest-value-but-hidden signal (P2-2): a critical/expired permit with no action owner.
const urgentUnassigned = (r: PermitRow) =>
  !r.void && !r.owner && (bandIs(r, '01') || (r.daysToExpiration != null && r.daysToExpiration < 0));
// A permit the county marked "VOID Deleted …" is dead — never open, gating, critical, or owing.
const isLive = (r: PermitRow) => !r.void;
// ISSUED inside 48h — the county's issue date, not our sync timestamps. No windowHint: the tab's
// time window keys off changedAt/appliedDate, so auto-setting it would compound two date axes.
// Calendar-day window (today/yesterday + post-dated tomorrow): the county issues with bare
// dates; a rolling-hours window dropped all of "yesterday" by late afternoon PT (live repro:
// county showed 21 issued in 48h, the KPI said 7).
const recentlyIssued = (r: PermitRow) => { const d = calendarDaysAgo(r.issueDate); return d != null && d <= 1 && d >= -1; };
// Segmentation the row data can honestly support: PermitRow carries no county class field
// (verified against joinCore 2026-06-12), but a permit tied to a named home model IS residential
// unit construction — everything else is site work / infrastructure / other non-unit scope.
const isHomePermit = (r: PermitRow) => !!(r.model || '').trim();

/** Overview takeaways — pure, computed from the scoped rows (never hardcoded). Exported for the
 *  regression harness. */
export function permitsTakeaways(rows: PermitRow[]): string[] {
  if (!rows.length) return [];
  const n = (x: number) => x.toLocaleString('en-US');
  const lots = new Set(rows.map(r => r.lotId).filter(Boolean)).size;
  const open = rows.filter(r => r.blocks && !r.void).length;
  const home = rows.filter(isHomePermit).length;
  const site = rows.length - home;
  const att = rows.filter(needsAttention).length;
  const crit = rows.filter(r => bandIs(r, '01')).length;
  const due = sum(rows, r => r.amountDue);
  const co = rows.filter(r => r.coVerified).length;
  const out: string[] = [];
  out.push(`${n(rows.length)} permit${rows.length === 1 ? '' : 's'} across ${n(lots)} lot${lots === 1 ? '' : 's'} — ${n(open)} open or gating construction (${pctOf(open, rows.length)}%).`);
  const byModel = new Map<string, number>();
  for (const r of rows) { const m = (r.model || '').trim(); if (m) byModel.set(m, (byModel.get(m) || 0) + 1); }
  const top = [...byModel.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} ${n(v)}`);
  if (home > 0) out.push(`${n(home)} ${home === 1 ? 'is a home permit' : 'are home permits'} on a named model${top.length > 1 ? ` (led by ${oxford(top)})` : ''}; ${n(site)} cover${site === 1 ? 's' : ''} site work or other non-unit scopes.`);
  out.push(`${n(att)} need${att === 1 ? 's' : ''} action (${n(crit)} critical) and ${due > 0 ? `${moneyCompact(due)} is owed to the county` : 'nothing is owed to the county'}; ${n(co)} ${co === 1 ? 'has' : 'have'} a verified CO.`);
  return out;
}

/** Permits tab config. `ctxData` carries the lot-grain inputs the KPIs need (no-permit gap)
 *  and the per-permit inspection counts for the "Inspections (n) →" affordance. */
export function makePermitsConfig(ctxData: { scopedUnitRows: UnitRow[]; inspCountByPermitNumber: Map<string, number>; activeNoPermit?: number }): GridConfig<PermitRow> {
  const { scopedUnitRows, inspCountByPermitNumber, activeNoPermit } = ctxData;

  const kpis: KpiDef<PermitRow>[] = [
    { label: 'Permits', define: 'All permits for the lots in view, inside the active time window and search.',
      compute: rows => ({ display: '', n: rows.length }) },
    // windowHint='48h': these two chips conceptually overlap the time window — selecting one
    // while the window is "All time" also sets "Last 48 hours" (DataGrid.toggleChip de-dup).
    // 'Discovered', not 'New': the county's 'new permits' means newly ISSUED (its own KPI) —
    // this one counts permits the sync first SAW in the window. The old label conflated them.
    { label: 'Discovered 48h', cluster: 'Worklist', axis: 'attention', windowHint: '48h',
      define: 'Permits first seen by the county sync within the last 48 hours.',
      compute: rows => ({ display: '', n: rows.filter(recentlyNew).length, tone: 'accent' }), filter: recentlyNew,
      hidden: rows => rows.length > 0 && rows.every(r => !r.firstSeenAt) },
    { label: 'Changed 48h', cluster: 'Worklist', axis: 'attention', windowHint: '48h',
      define: 'Permits with a meaningful iWorQ change (status, payment, inspection…) in the last 48 hours.',
      compute: rows => ({ display: '', n: rows.filter(recentlyChanged).length, tone: 'accent' }), filter: recentlyChanged,
      hidden: rows => rows.length > 0 && rows.every(r => !r.changedAt) },  // field not exposed yet → hide, never show a dead 0
    // No windowHint here: issuance is a county DATE, not a sync change-time — auto-setting the
    // changedAt-keyed window would silently hide a recently-issued permit whose change is older.
    { label: 'Issued 48h', cluster: 'Worklist', axis: 'attention',
      define: 'Permits the county issued within the last 48 hours (issue date).',
      compute: rows => ({ display: '', n: rows.filter(recentlyIssued).length, tone: 'accent' }), filter: recentlyIssued,
      hidden: rows => rows.length > 0 && rows.every(r => !r.issueDate) },
    // WINDOW-RELATIVE intake trio (Intake & payments perspective). Each measures its OWN date
    // axis against the CURRENTLY-selected time window (windowScope) — the strip recounts and
    // suffixes the label ("Discovered · 48h" → "Discovered · 7d") when the window changes, so the
    // operator's time filter scopes every Intake summary, not just Amount due. The fixed-48h chips
    // above stay frozen at 48h for the All perspective + worklist alerts (separate KPIs). compute()
    // is a 48h-default placeholder; the strip OVERRIDES the count via windowScope (see resolveKpi).
    { label: 'Discovered', cluster: 'Intake', axis: 'attention', windowScope: r => r.firstSeenAt,
      define: 'Permits first seen by the county sync within the selected time window (default 48h).',
      compute: rows => ({ display: '', n: rows.filter(recentlyNew).length, tone: 'accent' }),
      hidden: rows => rows.length > 0 && rows.every(r => !r.firstSeenAt) },
    { label: 'Changed', cluster: 'Intake', axis: 'attention', windowScope: r => r.changedAt,
      define: 'Permits with a meaningful iWorQ change (status, payment, inspection…) within the selected time window (default 48h).',
      compute: rows => ({ display: '', n: rows.filter(recentlyChanged).length, tone: 'accent' }),
      hidden: rows => rows.length > 0 && rows.every(r => !r.changedAt) },
    { label: 'Issued', cluster: 'Intake', axis: 'attention', windowScope: r => r.issueDate,
      define: 'Permits the county issued (issue date) within the selected time window (default 48h).',
      compute: rows => ({ display: '', n: rows.filter(recentlyIssued).length, tone: 'accent' }),
      hidden: rows => rows.length > 0 && rows.every(r => !r.issueDate) },
    { label: 'Needs attention', cluster: 'Worklist', axis: 'attention',
      define: 'Money owed, an open failed inspection, or an overdue/expired schedule.',
      compute: rows => ({ display: '', n: rows.filter(needsAttention).length, tone: 'maroon' }), filter: needsAttention,
      delta: rows => deltaCount(rows, needsAttention, recentlyChanged) },
    { label: 'Critical', cluster: 'Worklist', axis: 'attention',
      define: 'Permits in the 01 Critical urgency band.',
      compute: rows => ({ display: '', n: rows.filter(r => isLive(r) && bandIs(r, '01')).length, tone: 'maroon' }), filter: r => isLive(r) && bandIs(r, '01'),
      delta: rows => deltaCount(rows, r => isLive(r) && bandIs(r, '01'), recentlyChanged) },
    { label: 'Overdue', cluster: 'Worklist', axis: 'attention',
      define: 'Permits in the 02 Immediate audit / overdue urgency band.',
      compute: rows => ({ display: '', n: rows.filter(r => isLive(r) && bandIs(r, '02')).length, tone: 'amber' }), filter: r => isLive(r) && bandIs(r, '02') },
    { label: 'Amount due', cluster: 'Worklist', axis: 'attention', overFiltered: true,
      define: 'Total outstanding county balance across the permits currently shown (mirrors the filtered table).',
      // Sum effectiveDue (NOT raw amountDue) so the KPI shares ONE formula with the 'Amount due'
      // column + the Excel SUBTOTAL totals row — the headline can never diverge from the table.
      compute: rows => ({ display: moneyCompact(sum(rows.filter(isLive), effectiveDue)), tone: 'maroon' }), filter: r => isLive(r) && (effectiveDue(r) || 0) > 0 },
    { label: 'Expired', cluster: 'Worklist', axis: 'attention',
      define: 'Permits past their expiration date (negative days to expiration).',
      compute: rows => ({ display: '', n: rows.filter(r => isLive(r) && r.daysToExpiration != null && r.daysToExpiration < 0).length, tone: 'amber' }), filter: r => isLive(r) && r.daysToExpiration != null && r.daysToExpiration < 0 },
    {
      // Active lots (committed or building) with NO permit — the real worklist, not every drawable parcel.
      label: 'Active · no permit', cluster: 'Worklist', axis: 'attention',
      define: 'Active lots (committed buyer or build started) with no permit on file.',
      compute: rows => {
        // LOT-GRAIN, audit-verified definition (passed in from SiteMapApp — reads 23, not the
        // unit-grain 67 this used to compute). Fallback only if the prop is absent.
        if (activeNoPermit != null) return { display: '', n: activeNoPermit, tone: activeNoPermit > 0 ? 'amber' : 'accent' };
        const activeLots = new Set(scopedUnitRows.filter(isActiveUnit).map(u => u.lotId).filter(Boolean) as string[]);
        const lotsWithPermit = new Set(rows.map(r => r.lotId).filter(Boolean) as string[]);
        let gap = 0; activeLots.forEach(l => { if (!lotsWithPermit.has(l)) gap++; });
        return { display: '', n: gap, tone: gap > 0 ? 'amber' : 'accent' };
      },
    },
    { label: 'Open / gating', cluster: 'Portfolio',
      define: 'Open permits of a class that gates construction on the lot.',
      compute: rows => ({ display: '', n: rows.filter(r => r.blocks && isLive(r)).length }), filter: r => r.blocks && isLive(r) },
    { label: 'CO verified', cluster: 'Portfolio',
      define: 'Permits with a verified certificate of occupancy on file.',
      compute: rows => ({ display: '', n: rows.filter(r => r.coVerified).length, tone: 'accent' }), filter: r => r.coVerified },
    // Mix segmentation from data the row actually carries (no county class field is exposed):
    // a named home model = residential unit construction; the rest is site/infrastructure/other.
    { label: 'Home permits', cluster: 'Mix',
      define: 'Permits tied to a named home model — residential unit construction.',
      compute: rows => ({ display: '', n: rows.filter(isHomePermit).length }), filter: isHomePermit },
    { label: 'Site & other', cluster: 'Mix',
      define: 'Permits with no home model attached — site work, infrastructure, and other non-unit scopes.',
      compute: rows => ({ display: '', n: rows.filter(r => !isHomePermit(r)).length }), filter: r => !isHomePermit(r) },
  ];

  const columns: GridConfig<PermitRow>['columns'] = [
    { key: 'permit', header: 'Permit #', width: 120, sortable: true, section: 'Identity', value: r => r.number,
      render: (r, ctx) => <LinkText label={dash(r.number)} url={r.permitUrl} ctx={ctx} mono /> },
    // P2-1: one Lot identifier — "469 · B5-L21" — instead of two unlabeled columns.
    { key: 'lot', header: 'Lot', width: 168, sortable: true, section: 'Identity', value: r => r.lotName || r.blockLot,
      render: (r, ctx) => <LotCell blockLot={r.blockLot} lotId={r.lotId} name={r.lotName} extra={Math.max(0, r.lotIds.length - 1)} ctx={ctx} /> },
    { key: 'model', header: 'Model', width: 96, sortable: true, section: 'Identity', value: r => r.model, render: r => <span>{dash(r.model)}</span> },
    // P1-1: what changed (human one-liner + change-type pill colored by recency bucket).
    // value = the human SUMMARY (what the cell shows — CSV/Excel/PDF and quick-search read it;
    // the 'when' column carries the timestamp). sortValue = parsed epoch: changedAt renders
    // locale 12-hour ("6/12/2026 9:14am") — sorting raw strings put October before September.
    { key: 'changed', header: 'What changed', width: 210, sortable: true, defaultDesc: true, section: 'Activity', value: r => humanizeChange(r.changeSummary),
      sortValue: r => parseDate(r.changedAt) || null,
      render: r => r.changeSummary
        ? <div style={{ minWidth: 0 }} title={`${r.changeSummary}${r.changedAt ? ` · ${r.changedAt.slice(0, 16).replace('T', ' ')}` : ''}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: bucketColor(r.changeBucket), flex: '0 0 auto' }} />
              {/* display the humanized one-liner (WYSIWYG with the export); the raw diff stays in the title */}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{humanizeChange(r.changeSummary)}</span>
            </div>
            {/* the summary usually leads with the change type ("Payment posted: …") — repeating it
                in the eyebrow is double-speak, so only show the eyebrow when it adds information */}
            {r.changeType && !r.changeSummary.trim().toLowerCase().startsWith(r.changeType.trim().toLowerCase())
              && <span className="eyebrow" style={{ color: T.inkSoft }}>{r.changeType}</span>}
          </div>
        : <span style={{ color: T.inkSoft }}>—</span> },
    // The change TIME, visible — "What changed" says what, this says when (sorts by the same
    // timestamp, so either header click gives true newest-first). Relative for the last 48h
    // because "3h ago" answers the operator's real question faster than a date does.
    { key: 'when', header: 'Changed at', width: 96, align: 'right', sortable: true, defaultDesc: true, hideWhenEmpty: true, section: 'Activity',
      value: r => isoWhen(r.changedAt), sortValue: r => parseDate(r.changedAt) || null,
      render: r => r.changedAt
        ? <span className="num" title={r.changedAt.slice(0, 16).replace('T', ' ')}>{fmtWhen(r.changedAt)}</span>
        : <span style={{ color: T.inkSoft }}>—</span> },
    // value = the displayed band name (exports/search see "Critical", not a bare rank number);
    // sortValue = Closeout Action Rank (§5) keeps the operational ordering.
    { key: 'urgency', header: 'Urgency', width: 150, sortable: true, section: 'Status', value: r => r.void ? 'Void' : bandName(r.urgencyBand),
      sortValue: r => r.void ? 9999 : r.actionRank,
      render: r => r.void
        ? <span title="County marked this permit VOID / Deleted"><Pill label="Void" color={T.inkSoft} /></span>
        : <span title={(r.urgencyBand || '').replace(/^\d+\s*/, '') || undefined}>
            <Pill label={bandName(r.urgencyBand)} color={bandColor(r.urgencyBand)} /></span> },
    // Open/Closed lifecycle from the county formula — a permit row always reads at least 'Open'.
    { key: 'openClosed', header: 'Open/Closed', width: 130, sortable: true, section: 'Status', value: r => r.openClosed || 'Open',
      render: r => { const v = r.openClosed || 'Open'; return <Pill label={v} color={openClosedColor(v)} />; } },
    { key: 'co', header: '% to CO', width: 130, sortable: true, section: 'Status', hideWhenEmpty: true, value: r => pct100(r.pctToCO),
      render: r => <ProgressBar pct={pct100(r.pctToCO)} caption={r.closeoutStage} /> },
    { key: 'sched', header: 'Schedule', width: 160, sortable: true, section: 'Status', value: r => r.schedule,
      render: r => r.schedule ? <span title={r.schedule}><Pill label={shortSchedule(r.schedule)} color={T.inkSoft} /></span> : <span style={{ color: T.inkSoft }}>—</span> },
    { key: 'next', header: 'Next insp', width: 168, value: r => r.nextInsp,
      render: (r, ctx) => {
        const n = inspCountByPermitNumber.get(r.number) ?? 0;
        return (
          <div style={{ minWidth: 0 }} title={r.nextInsp || r.nextSched || undefined}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dash(r.nextInsp || r.nextSched)}</div>
            {n > 0 && <button className="lfe-press" onClick={e => { e.stopPropagation(); ctx.jumpToInspections(r.number); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: T.accent, fontSize: 11, fontWeight: 600 }}>
              Inspections ({n}) →</button>}
          </div>
        );
      } },
    // P1-2: applied date (newest-first sort proxy).
    { key: 'applied', header: 'Applied', width: 96, align: 'right', sortable: true, defaultDesc: true, section: 'Activity', value: r => r.appliedDate,
      sortValue: r => parseDate(r.appliedDate) || null,
      render: r => <span className="num" title={(r.appliedDate || '').slice(0, 10)} style={{ color: T.inkSoft, fontSize: 11.5 }}>{fmtDate(r.appliedDate)}</span> },
    // Issued — the county date the portal sorts by; blank = application still pending issuance.
    { key: 'issued', header: 'Issued', width: 92, align: 'right', sortable: true, defaultDesc: true, section: 'Activity', hideWhenEmpty: true,
      value: r => r.issueDate, sortValue: r => parseDate(r.issueDate) || null,
      render: r => <span className="num" title={(r.issueDate || '').slice(0, 10)} style={{ color: T.inkSoft, fontSize: 11.5 }}>{fmtDate(r.issueDate)}</span> },
    // Completed — the county's closeout date (CO'd); the end of the Applied → Issued → Completed arc.
    { key: 'completed', header: 'Completed', width: 96, align: 'right', sortable: true, defaultDesc: true, section: 'Activity', hideWhenEmpty: true,
      value: r => r.completedDate, sortValue: r => parseDate(r.completedDate) || null,
      render: r => <span className="num" title={(r.completedDate || '').slice(0, 10)} style={{ color: T.inkSoft, fontSize: 11.5 }}>{fmtDate(r.completedDate)}</span> },
    { key: 'due', header: 'Amount due', width: 100, align: 'right', sortable: true, section: 'Money', value: r => effectiveDue(r),
      render: r => <span className="num" title={r.feeBalance != null ? `Fee balance ${money(r.feeBalance)}` : undefined}
        style={{ color: (effectiveDue(r) || 0) > 0 ? T.maroon : T.ink }}>{money(effectiveDue(r))}</span> },
    { key: 'exp', header: 'Expires (days)', width: 106, align: 'right', sortable: true, section: 'Status', value: r => r.daysToExpiration,
      render: r => r.daysToExpiration == null ? <span style={{ color: T.inkSoft }}>—</span>
        : <span className="num" title={r.expirationDate} style={{ color: r.daysToExpiration < 0 ? T.maroon : T.ink }}>{r.daysToExpiration}d</span> },
    { key: 'blocks', header: 'Gate', width: 64, align: 'right', sortable: true, section: 'Status',
      value: r => r.blocks ? 'Open / gating' : '', sortValue: r => r.blocks ? 1 : 0,
      render: r => r.blocks ? <span title="Open / gating the next phase">⛔</span> : <span style={{ color: T.inkSoft }}>—</span> },
    { key: 'owner', header: 'Owner · SLA', width: 150, hideWhenEmpty: true, value: r => urgentUnassigned(r) ? 'unassigned' : (r.owner || null),
      render: r => urgentUnassigned(r)
        ? <span title="Critical/expired with no action owner" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: T.maroon, color: T.onMaroon }}>⚠ unassigned</span>
        : (
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.owner || undefined}>{dash(r.owner)}</div>
            {r.sla ? <span title={r.sla}><Pill label={r.sla} color={T.inkSoft} /></span> : null}
          </div>
        ) },
    // Downloadable county documents (exposure-gated: columns vanish until the fields are exposed).
    { key: 'permitpdf', header: 'Permit PDF', width: 104, hideWhenEmpty: true, sortable: true, section: 'Documents',
      value: r => r.signedPermitDocs.map(f => f.filename).join('; '),
      render: (r, ctx) => <DocsCell docs={r.signedPermitDocs} ctx={ctx} /> },
    { key: 'noc', header: 'NOC', width: 88, hideWhenEmpty: true, sortable: true, section: 'Documents',
      value: r => r.nocDocs.map(f => f.filename).join('; '),
      render: (r, ctx) => <DocsCell docs={r.nocDocs} ctx={ctx} /> },
    { key: 'cocert', header: 'CO', width: 56, align: 'right', hideWhenEmpty: true, value: r => r.coVerified ? 'CO verified' : '',
      sortValue: r => r.coVerified ? 1 : 0,
      render: (r, ctx) => r.coVerified
        ? (r.coUrl ? <button className="lfe-press" onClick={e => { e.stopPropagation(); ctx.openUrl(r.coUrl); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontWeight: 700 }}>✓</button>
          : <span style={{ color: T.accent, fontWeight: 700 }}>✓</span>)
        : <span style={{ color: T.inkSoft }}>—</span> },
  ];

  return {
    columns, kpis,
    // Styled-export hints (export/dockWorkbook + buildDockPdf): county money as currency, the
    // Applied/Issued/Completed county dates as REAL Excel dates, urgency cells toned like the pill.
    excel: {
      sheetTitle: 'Permits',
      money: ['due'],
      moneyFmt: '$#,##0.00;($#,##0.00);"–"',   // county balances are exact cents — this is the payments view
      date: ['applied', 'issued', 'completed'],
      datetime: ['when'],                       // "Changed at" → real Excel date+time, sortable as a timestamp
      tone: {
        urgency: r => {
          const b = (r.urgencyBand || '').slice(0, 2);
          return b === '01' || b === '02' ? 'blocked'
            : b === '03' || b === '04' || b === '05' ? 'progress'
            : b === '80' ? 'good' : b === '90' ? 'neutral' : undefined;
        },
      },
      // Print pin for the uncurated view: identity + activity + the urgency/closeout/money story.
      pdfColumns: ['permit', 'lot', 'model', 'changed', 'urgency', 'co', 'due', 'issued'],
      pdfHeaders: { co: '% to CO', due: 'Due' },
    },
    groupKey: r => r.void ? 'Void' : bandName(r.urgencyBand),
    dateOf: r => r.changedAt || r.appliedDate,
    groupOrder: [...BAND_ORDER.map(bandName), 'Void'],
    defaultSort: { key: 'changed', dir: 'desc' },  // newest activity first within each band
    rowLotId: r => r.lotId,
    rowKey: r => r.id,
    ariaLabel: r => `Permit ${r.number || '—'}, lot ${r.blockLot || '—'}, ${r.urgencyBand || 'unbanded'}${(r.amountDue || 0) > 0 ? `, ${money(r.amountDue)} due` : ''}`,
    groupSubtotal: rows => money(sum(rows, r => r.amountDue)),
    groupOptions: [
      { key: 'band', label: 'Urgency band', keyOf: r => r.void ? 'Void' : bandName(r.urgencyBand), col: 'urgency' },
      { key: 'changed', label: 'Change recency', keyOf: r => r.changeBucket || 'No meaningful change' },
      { key: 'changetype', label: 'Change type', keyOf: r => r.changeType || '—' },
      { key: 'lot', label: 'Lot', keyOf: r => r.lotName ? `${r.lotName} · ${r.blockLot}` : (r.blockLot || '—'), col: 'lot' },
      { key: 'model', label: 'Model', keyOf: r => r.model || '—', col: 'model' },
      { key: 'schedule', label: 'Schedule status', keyOf: r => r.schedule || '—', col: 'sched' },
      { key: 'owner', label: 'Action owner', keyOf: r => r.owner || 'Unassigned', col: 'owner' },
      { key: 'stage', label: 'Closeout stage', keyOf: r => r.closeoutStage || '—' },
      { key: 'blocks', label: 'Gating', keyOf: r => r.blocks ? 'Open / gating' : 'Not gating' },
    ],
    groupStats: rows => {
      const n = rows.length || 1;
      const avgCO = Math.round(rows.reduce((s, r) => s + (pct100(r.pctToCO) || 0), 0) / n);
      const exp = rows.filter(r => r.daysToExpiration != null && r.daysToExpiration < 0).length;
      const blk = rows.filter(r => r.blocks).length;
      const chg = rows.filter(recentlyChanged).length;
      const chip = (l: string, c: string) => <span className="num" style={{ fontSize: 11, color: c, fontWeight: 600 }}>{l}</span>;
      return <>{chip(`${avgCO}% avg CO`, T.inkSoft)}{chg > 0 && chip(`${chg} changed 48h`, T.accent)}{exp > 0 && chip(`${exp} expired`, T.amber)}{blk > 0 && chip(`${blk} gating`, T.maroon)}</>;
    },
    // field:value facets for the quick-filter input (documented in its title). Owner accepts
    // `owner:unassigned`; flag facets accept yes/has ↔ no/none (gate:yes, noc:none, pdf:none).
    facets: {
      model: facetText(r => r.model),
      lot: facetText(r => `${r.lotName} ${r.blockLot}`),
      permit: facetText(r => r.number),
      owner: facetPerson(r => r.owner),
      schedule: facetText(r => r.schedule),                  // schedule:expired, schedule:overdue
      band: facetText(r => r.urgencyBand),                   // band:critical
      openClosed: facetText(r => r.openClosed),              // openClosed:open / openClosed:closed
      gate: facetFlag(r => r.blocks),
      noc: facetFlag(r => r.nocDocs.length > 0),
      pdf: facetFlag(r => r.signedPermitDocs.length > 0),
      change: facetText(r => `${r.changeType} ${r.changeBucket} ${r.changeSummary}`),  // change:inspection, change:fee, change:payment
      issued: facetDate(r => r.issueDate, parseDate),        // issued:none / issued:has / issued:48h|7d|2w|month
      applied: facetDate(r => r.appliedDate, parseDate),
    },
    // Starter views — applied like saved views, marked preset (not deletable).
    presetViews: [
      { name: 'Morning triage', preset: true, window: '48h', sort: { key: 'changed', dir: 'desc' } },
      { name: 'Money owed', preset: true, chips: ['Amount due'], sort: { key: 'due', dir: 'desc' } },
      { name: 'Doc gaps', preset: true, search: 'noc:none', sort: { key: 'co', dir: 'desc' } },
    ],
    // Curated sub-views (pill row under the tab bar; "All" = this full config).
    perspectives: [
      { key: 'intake', label: 'Intake & payments',
        blurb: 'New applications, fresh issuances, and county balances — the permit front door.',
        // Window-RELATIVE lifecycle KPIs: each recounts on its own date axis as the time window
        // changes (Discovered=firstSeenAt, Changed=changedAt, Issued=issueDate), alongside Amount
        // due. Default window 48h, so out-of-the-box they read today's numbers; switching to
        // "Last 7 days" recounts all three over 7 days. (The fixed-48h chips live on the All view.)
        kpiKeys: ['Discovered', 'Changed', 'Issued', 'Amount due'],
        columnKeys: ['permit', 'lot', 'model', 'changed', 'when', 'applied', 'issued', 'due', 'permitpdf', 'noc'],
        defaults: { sortKey: 'issued', sortDir: 'desc', window: '48h' } },
      { key: 'issues', label: 'Issues',
        blurb: 'Problems to fix — critical, overdue, expired, and unpaid permits, most urgent first.',
        kpiKeys: ['Needs attention', 'Critical', 'Overdue', 'Expired', 'Active · no permit', 'Open / gating'],
        columnKeys: ['permit', 'lot', 'changed', 'urgency', 'co', 'sched', 'next', 'exp', 'owner'],
        defaults: { sortKey: 'urgency', sortDir: 'asc' } },
      { key: 'overview', label: 'Overview',
        blurb: 'The big picture — totals, home vs site mix, and portfolio health.',
        kpiKeys: ['Permits', 'Open / gating', 'CO verified', 'Home permits', 'Site & other'],
        columnKeys: ['permit', 'lot', 'model', 'co', 'sched', 'applied', 'issued'],
        defaults: { groupBy: 'blocks' },
        takeaways: permitsTakeaways },
    ],
    empty: 'No permits for this selection.',
  };
}
